"use client";
import { useTransition } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleInfo } from "@fortawesome/pro-solid-svg-icons/faCircleInfo";
import { faCommentSms } from "@fortawesome/pro-solid-svg-icons/faCommentSms";
import { faEnvelopeOpen } from "@fortawesome/pro-solid-svg-icons/faEnvelopeOpen";
import { faPhone } from "@fortawesome/pro-solid-svg-icons/faPhone";
import { faTriangleExclamation } from "@fortawesome/pro-solid-svg-icons/faTriangleExclamation";
import { faClock } from "@fortawesome/pro-regular-svg-icons/faClock";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import CN from "classnames";
import { styled } from "@mui/material/styles";
import { DreamMuiLargeListItem } from "./DreamMuiLargeListItem";
import { useBusinessHours } from "../../hooks/useBusinessHours";
import { useContactPhone } from "../../hooks/useContactPhone";
import { DreamMuiStackForButtons } from "./DreamMuiStackForButtons";
import { faSync } from "@fortawesome/pro-solid-svg-icons/faSync";
import { externalUrls } from "@portal/app/_common/lib/urls";
import { faHome } from "@fortawesome/pro-solid-svg-icons/faHome";

export const DreamMuiHandledErrorRefresh = ({
  onRetry,
  homeHref = "/",
  ...props
}: React.ComponentProps<typeof DreamMuiHandledError> & {
  onRetry?: () => void;
  homeHref?: string;
}) => {
  const [isRetrying, startTransition] = useTransition();

  return (
    <DreamMuiHandledError
      title="There was an error"
      hasClickableAdditionalOptions
      additionalOptions={
        <Box mb={2} display="grid" gap={2}>
          {onRetry && (
            <Button
              variant="contained"
              startIcon={<FontAwesomeIcon icon={faSync} />}
              disabled={isRetrying}
              aria-busy={isRetrying}
              onClick={() => startTransition(onRetry)}
            >
              {isRetrying ? "Trying again…" : "Try again"}
            </Button>
          )}
          <DreamMuiLargeListItem
            icon={<FontAwesomeIcon icon={faSync} />}
            title="Refresh the page"
            onClick={() => location.reload()}
          />
          <DreamMuiLargeListItem
            icon={<FontAwesomeIcon icon={faHome} />}
            title="Back to home page"
            href={homeHref}
          />
        </Box>
      }
      data-testid="handled-error"
      {...props}
    />
  );
};

export const DreamMuiHandledError = ({
  title = "There's a problem on our end",
  content,
  additionalOptions,
  hasClickableAdditionalOptions = false,
  showReturnLaterOption = false,
  withBorder = false,
  className,
  errorOnly,
  ...propsToWrapperBox
}: Omit<React.ComponentProps<typeof Wrapper>, "content"> & {
  title?: React.ReactNode;
  /**
   * Optional explanation for why the issue occurred
   */
  content?: React.ReactNode;
  /**
   * Pass True to show an option that explains the user that they can come
   * back later to see if the issue has resolved itself. Use this only in
   * situations where this might actually help
   */
  showReturnLaterOption?: boolean;
  /**
   * Pass True to align other automatically-added options as `clickable`.
   * Otherwise, if the `additionalOptions` you add is clickable, it will not be
   * aligned with the others.
   */
  hasClickableAdditionalOptions?: boolean;
  /**
   * If you'd like to present the user with additional options (beyond waiting
   * or contacting support), you can return a single/array of <DreamMuiLargeListItem>
   */
  additionalOptions?: React.ReactNode;
  /**
   * If True, a border is added.
   */
  withBorder?: boolean;
  className?: string;
  errorOnly?: true;
}) => {
  const { phoneFormatted, phoneForHTML } = useContactPhone();
  const {
    isDreamboundOpen,
    todayHoliday,
    tomorrowHoliday,
    dreamboundBizHoursStartStr,
    dreamboundBizHoursEndStr,
    localTimezoneStr,
  } = useBusinessHours();

  return (
    <Wrapper className={CN({ bordered: withBorder }, className)} {...propsToWrapperBox}>
      <CenteredWrapper>
        <Icon>
          <FontAwesomeIcon icon={faTriangleExclamation} />
        </Icon>
        <Typography variant="page_title" sx={{ mt: 2, mb: 1, color: "error.main" }}>
          {title}
        </Typography>
        {!errorOnly && (
          <>
            {content}
            <Typography variant="new_h2" sx={{ mt: 4 }}>
              What you can do
            </Typography>
            {additionalOptions}
            {showReturnLaterOption && (
              <DreamMuiLargeListItem
                icon={<FontAwesomeIcon icon={faClock} />}
                title="Try doing this later"
                variant={hasClickableAdditionalOptions ? "clickable" : undefined}
              >
                <Typography paragraph>
                  Issues like this are often caused by our systems being overloaded. Waiting 15-30
                  minutes can sometimes fix the issue by itself.
                </Typography>
              </DreamMuiLargeListItem>
            )}
            <DreamMuiLargeListItem
              icon={<FontAwesomeIcon icon={faCircleInfo} />}
              title={isDreamboundOpen ? "Get help right away" : "Get help from Dreambound"}
              variant={hasClickableAdditionalOptions ? "clickable" : undefined}
            >
              {isDreamboundOpen ? (
                <Typography paragraph>
                  We&apos;re here until {dreamboundBizHoursEndStr}, and our team can look into this
                  and help you get going.
                </Typography>
              ) : todayHoliday ? (
                <Typography paragraph>
                  We&apos;re closed for {todayHoliday}, but we can follow up immediately after we
                  return.
                </Typography>
              ) : tomorrowHoliday ? (
                <Typography paragraph>
                  We&apos;re closed tomorrow for {tomorrowHoliday}, but we can follow up immediately
                  after we return.
                </Typography>
              ) : (
                <Typography paragraph>
                  We&apos;re back at {dreamboundBizHoursStartStr}, and we can look into this as soon
                  as possible.
                </Typography>
              )}

              <DreamMuiStackForButtons sx={{ mt: 2 }}>
                <Button
                  component="a"
                  variant="outlined"
                  startIcon={<FontAwesomeIcon icon={faCommentSms} />}
                  href={"sms:" + phoneForHTML}
                >
                  Text {phoneFormatted}
                </Button>
                <Button
                  component="a"
                  variant="outlined"
                  startIcon={<FontAwesomeIcon icon={faEnvelopeOpen} />}
                  href={externalUrls.MailToEmail}
                >
                  support@dreambound.com
                </Button>
                {isDreamboundOpen && (
                  <Button
                    component="a"
                    variant="outlined"
                    startIcon={<FontAwesomeIcon icon={faPhone} />}
                    href={"tel:" + phoneForHTML}
                  >
                    Call {phoneFormatted}
                  </Button>
                )}
              </DreamMuiStackForButtons>
              <Typography variant="disclaimer" sx={{ mt: 1 }}>
                We&apos;re open {dreamboundBizHoursStartStr}&ndash;
                {dreamboundBizHoursEndStr} {localTimezoneStr} every day except holidays.{" "}
                {!!tomorrowHoliday && `Note: We're closed tomorrow for ${tomorrowHoliday}.`}
              </Typography>
            </DreamMuiLargeListItem>
          </>
        )}
      </CenteredWrapper>
    </Wrapper>
  );
};

const Wrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2rem;

  ${({ theme }) => theme.breakpoints.down("md")} {
    padding: var(--horizontal-padding);
  }

  &.bordered {
    border: 1px solid ${({ theme }) => theme.palette.dream.grayLight};
    border-radius: 0.5rem;
  }
`;

const CenteredWrapper = styled(Box)`
  width: 100%;
  max-width: ${({ theme }) => theme.breakpoints.values.md}px;
`;

const Icon = styled(Box)`
  font-size: 3rem;
  color: ${({ theme }) => theme.palette.error.main};
`;
